import React, { useState } from "react";
import { Stack, Box, Typography, Button, Link, Popover, List, ListItem, ListItemIcon, ListItemText, Snackbar, Alert } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Skeleton from "@mui/material/Skeleton";
import { useApiCall } from "~/single-page/hooks/useApiCall";
import { useParams, Link as RouterLink } from 'react-router-dom';
import NoItemsMessage from "~/components/NoItemsMessage";
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FacebookIcon from '@mui/icons-material/Facebook';
import XIcon from '@mui/icons-material/X';
import TwitterIcon from '@mui/icons-material/Twitter';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import InstagramIcon from '@mui/icons-material/Instagram';
import GalleryCards from "~/components/NGOs/GalleryCards";
import { useDonationModal } from '~/single-page/hooks/useDonationModal';
import { useVolunteerModal } from '~/single-page/hooks/useVolunteerModal';
import ODSsCards from "~/components/ODSs/Cards";
import { APP_ROUTES } from "~/single-page/constants/routes";
import ActionManagementsClient from "../api/ActionManagementsClient";
import { useScrollToSection } from '~/single-page/hooks/useScrollToSection';
import DateRangeIcon from '@mui/icons-material/DateRange';
import PinDropIcon from '@mui/icons-material/PinDrop';

const LoadingSkeleton = () => (
  <Grid container spacing={3}>
    <Grid item xs={12} md={6}>
      <Skeleton variant="rectangular" height={118} />
    </Grid>
  </Grid>
);

const ActionButton = ({ variant, onClick, children, outlined, component, to, endIcon, disabled }) => (
  <Button
    variant={variant}
    onClick={onClick}
    component={component}
    to={to}
    fullWidth
    endIcon={endIcon}
    disabled={disabled}
    sx={{
      backgroundColor: outlined ? 'transparent' : '#294BB6',
      color: outlined ? '#294BB6' : 'white',
      borderColor: '#294BB6',
      '&:hover': {
        backgroundColor: outlined ? 'transparent' : '#294BB6',
        borderColor: '#294BB6',
        opacity: 0.8
      },
      '&:disabled': {
        backgroundColor: disabled && !outlined ? '#ccc' : 'transparent',
        color: disabled ? '#666' : (outlined ? '#294BB6' : 'white'),
        borderColor: disabled ? '#ccc' : '#294BB6',
      },
      py: 1.5,
      textTransform: 'none',
      fontWeight: outlined ? 600 : 700
    }}
  >
    {children}
  </Button>
);

export default function ActionManagementDetailPage() {
  const { id } = useParams();
  useScrollToSection();
  const [shareAnchorEl, setShareAnchorEl] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const {
    data: action = null,
    success,
    pending,
  } = useApiCall({
    apiCall: () => id ? ActionManagementsClient.actionManagement(id) : Promise.reject('ID não fornecido'),
    init: true,
    deps: [id],
  });

  // Verificar se a ação já ocorreu
  const isActionPast = () => {
    if (!action) return false;
    
    const now = new Date();
    const endDate = action.end_date ? new Date(action.end_date) : null;
    
    if (endDate) {
      return now > endDate;
    }
    
    return false;
  };

  const { handleOpenDonationModal, DonationModalComponent } = useDonationModal();
  const { handleOpenVolunteerModal, VolunteerModalComponent } = useVolunteerModal();

  const handleShareClick = (event) => {
    setShareAnchorEl(event.currentTarget);
  };

  const handleShareClose = () => {
    setShareAnchorEl(null);
  };

  const shareOpen = Boolean(shareAnchorEl);
  const shareId = shareOpen ? 'share-popover' : undefined;
  
  const currentUrl = window.location.href;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(currentUrl);
    setSnackbarMessage('Link copiado para a área de transferência!');
    setSnackbarOpen(true);
    handleShareClose();
  };

  const handleShareFacebook = () => {
    // const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`;
    // window.open(url, '_blank', 'width=600,height=400');
    // handleShareClose();

    const facebookLink = action.ong?.facebook_link;

    if (facebookLink) {
      window.open(facebookLink, '_blank');
    } else {
      const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`;
      window.open(url, '_blank', 'width=600,height=400');
    }
    handleShareClose();
  };

  const handleShareX = () => {
    const text = `Conheça a ação "${action.title}" da ${action.ong.name} no Bora Impactar!`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(currentUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
    handleShareClose();
  };

  const handleShareWhatsApp = () => {
    const text = `Conheça a ação "${action.title}" da ${action.ong.name} no Bora Impactar: ${currentUrl}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
    handleShareClose();
  };

  const handleShareInstagram = () => {   
    const instagramLink = action.ong?.instagram_link;    
    
    const instagramUrl = instagramLink ? instagramLink : 'https://www.instagram.com/';
    window.open(instagramUrl, '_blank');
    handleShareClose();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  if (pending) return <LoadingSkeleton />;
  if (!success || !action) return <NoItemsMessage itemName="ação" gender="feminino" />;

  const hasAccountabilityImages = Array.isArray(action.accountability_images_url) && action.accountability_images_url.length > 0;

  return (
    <Stack spacing={4} sx={{ maxWidth: '100%', px: 2, py: 3 }}>
      {/* Coluna da esquerda */}
      <Box sx={{ 
        display: 'flex', 
        gap: 3, 
        flexDirection: { xs: 'column', md: 'row' }
      }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            {action.title}
          </Typography>

          <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
            {action.ong.name}
          </Typography>

          <Typography variant="body1" sx={{ mt: 3 }}>
            {action.description}
          </Typography>


        {/* Seção de Itens */}
        {action.has_items && action.items_hash && Object.keys(action.items_hash).length > 0 && (
          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="h6" color="secondary" sx={{ mb: 1 }}>
              Itens da Ação:
            </Typography>
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              ml: 2
            }}>
              {Object.entries(action.items_hash).map(([key, value], index) => (
                <Box key={index} sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: 1 
                }}>
                  <Box component="span" sx={{ 
                    fontSize: '1.5rem', 
                    color: 'secondary.main',
                    lineHeight: 1 
                  }}>
                    •
                  </Box>
                  <Typography variant="body1">
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}


          
          {(action.start_date || action.location) && (
            <Box sx={{ mt: 5, mb: -5, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {action.start_date && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {!action.end_date || action.start_date === action.end_date ? (
                    <>
                      <Box 
                        component="span" 
                        sx={{ 
                          color: 'primary.main', 
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <DateRangeIcon color="secondary" />
                      </Box>
                      <Typography variant="body1">
                        {new Date(action.start_date).toLocaleDateString('pt-BR', { 
                          day: '2-digit', 
                          month: '2-digit', 
                          year: 'numeric'
                        })}{", "}
                        {new Date(action.start_date).toLocaleTimeString('pt-BR', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Box 
                        component="span" 
                        sx={{ 
                          color: 'primary.main', 
                          display: 'flex',
                          alignSelf: 'center',
                          alignItems: 'center'
                        }}
                      >
                        <DateRangeIcon color="secondary" />
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body1">
                          {new Date(action.start_date).toLocaleDateString('pt-BR', { 
                            day: '2-digit', 
                            month: '2-digit', 
                            year: 'numeric'
                          })}{", "}
                          {new Date(action.start_date).toLocaleTimeString('pt-BR', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                          {/* {" - "} */}
                        </Typography>
                        <Typography variant="body1">
                          {new Date(action.end_date).toLocaleDateString('pt-BR', { 
                            day: '2-digit', 
                            month: '2-digit', 
                            year: 'numeric'
                          })}{", "}
                          {new Date(action.end_date).toLocaleTimeString('pt-BR', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              )}

              {action.location && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box 
                    component="span" 
                    sx={{ 
                      color: 'primary.main', 
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <PinDropIcon color="secondary" />
                  </Box>
                  <Typography variant="body1">
                    {action.location}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          <Box sx={{ mt: 14 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Impactar outras vidas: seja um voluntário(a)!
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ fontSize: '0.875rem' }}
            >
              Você é a pessoa que faltava no nosso grupo para fazer nossa ação ainda mais gigante! 
              Traga a sua disposição e habilidades para impactar outras vidas. Esperamos por você!
              {action.skills?.length > 0 && (
                <>
                  <br />
                  Caso tenha habilidades em: {action.skills.map(skill => skill.name).join(', ')}.
                </>
              )}
            </Typography>
          </Box>
        </Box>

        {/* Coluna da direita - Contém os botões e o custo total */}
        <Box sx={{ 
          width: { xs: '100%', md: '300px' }, 
          position: 'relative',
          '@media (min-width: 413px) and (max-width: 899px)': {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: '400px'
          }
        }}>
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: 2,
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              boxShadow: 3,
              width: '100%',
              alignSelf: 'flex-start',
              mb: 3,
              zIndex: 1,
              '@media (min-width: 413px) and (max-width: 899px)': {
                maxWidth: '400px'
              }
            }}
          >
            <ActionButton 
              variant="contained" 
              onClick={handleOpenDonationModal}
              disabled={isActionPast()}
            >
              Quero doar
            </ActionButton>

            <ActionButton 
              variant="contained" 
              onClick={handleOpenVolunteerModal}
              disabled={isActionPast()}
            >
              Quero ser um voluntário
            </ActionButton>

            <ActionButton 
              variant="outlined" 
              outlined 
              onClick={handleShareClick}
              aria-describedby={shareId}
              endIcon={<ShareIcon sx={{ color: '#294BB6' }} />}
            >
              Compartilhar
            </ActionButton>

            <Popover
              id={shareId}
              open={shareOpen}
              anchorEl={shareAnchorEl}
              onClose={handleShareClose}
              anchorOrigin={{
                vertical: 'center',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'center',
                horizontal: 'right',
              }}
            >
              <List sx={{ width: 250, p: 0 }}>
                <ListItem button onClick={handleCopyLink}>
                  <ListItemIcon>
                    <ContentCopyIcon />
                  </ListItemIcon>
                  <ListItemText primary="Copiar link" />
                </ListItem>
                <ListItem button onClick={handleShareFacebook}>
                  <ListItemIcon>
                    <FacebookIcon sx={{ color: "#1877F2" }} />
                  </ListItemIcon>
                  <ListItemText primary="Facebook" />
                </ListItem>
                <ListItem button onClick={handleShareX}>
                  <ListItemIcon>
                    <XIcon sx={{ color: "#000000" }} />
                  </ListItemIcon>
                  <ListItemText primary="X" />
                </ListItem>
                <ListItem button onClick={handleShareInstagram}>
                  <ListItemIcon>
                    <InstagramIcon sx={{ color: "#E4405F" }} />
                  </ListItemIcon>
                  <ListItemText primary="Instagram" />
                </ListItem>
                <ListItem button onClick={handleShareWhatsApp}>
                  <ListItemIcon>
                    <WhatsAppIcon sx={{ color: "#25D366" }} />
                  </ListItemIcon>
                  <ListItemText primary="WhatsApp" />
                </ListItem>
              </List>
            </Popover>

            <ActionButton
              variant="outlined"
              outlined
              component={RouterLink}
              to={`${APP_ROUTES.NGO}/${action.ong.id}`}
            >
              {action.ong.is_formalized ? "Conheça a ONG" : "Conheça o projeto"}
            </ActionButton>
          </Box>

          {action.total_cost > 0 && (
            <Box 
              sx={{ 
                backgroundColor: '#4A9BE8', 
                borderRadius: 2,
                p: { xs: 1, sm: 1.5 },
                color: 'white',
                textAlign: 'center',
                width: '100%',
                maxWidth: '300px',
                zIndex: 0,
                position: 'relative',
                overflow: 'hidden',
                minWidth: 0,
                '@media (min-width: 413px) and (max-width: 899px)': {
                  maxWidth: '400px',
                  width: '100%',
                  alignSelf: 'center'
                }
              }}
            >
              <Typography 
                variant="h6" 
                component="div" 
                sx={{ 
                  fontWeight: 500,
                  mb: 0.3,
                  fontSize: { xs: '0.9rem', sm: '1.25rem' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                Custo total da Ação :
              </Typography>
              <Typography 
                variant="h3" 
                component="div" 
                sx={{ 
                  fontWeight: 'bold',
                  mb: 0.3,
                  fontSize: { 
                    xs: '1.5rem',    // 24px - mobile
                    sm: '2.2rem',    // 35px - tablet (reduzido de 2.5rem)
                    md: '2.4rem',    // 38px - desktop (reduzido de 3rem)
                    lg: '2.6rem'     // 42px - telas muito grandes
                  },
                  lineHeight: { xs: 1.2, sm: 1.3 },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                  '@media (min-width: 900px)': {
                    fontSize: 'clamp(1.8rem, 4vw, 2.4rem)', // Responsivo entre 1.8rem e 2.4rem
                  }
                }}
              >
                R${new Intl.NumberFormat('pt-BR', { 
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }).format(action.total_cost || 0)}
              </Typography>
              <Typography 
                variant="body2" 
                component="div" 
                sx={{ 
                  fontSize: { xs: '0.75rem', sm: '0.9rem' },
                  opacity: 0.9,
                  lineHeight: 1.2,
                  px: { xs: 0.5, sm: 1 },
                  overflow: 'hidden',
                  textAlign: 'center',
                }}
              >
                De pouquinho em pouquinho você pode nos ajudar, <strong>qualquer contribuição é válida.</strong>
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {Array.isArray(action.gallery_images_url) && action.gallery_images_url.length > 0 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Galeria
          </Typography>
          <GalleryCards action={action} type="gallery" />
        </Box>
      )}

      {hasAccountabilityImages && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Prestação de Contas
          </Typography>
          <GalleryCards action={action} type="accountability" />
        </Box>
      )}

      <Box>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            mb: 2 
          }}
        >
          <Typography variant="h6">
            Objetivos para o Desenvolvimento Sustentável da ação
          </Typography>
          <Link
            href="https://brasil.un.org/pt-br/sdgs"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ 
              textDecoration: 'none',
              color: 'primary.main',
              fontSize: '0.875rem',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            Saiba mais
          </Link>
        </Box>
        <ODSsCards odss={action.sustainable_development_goals} />
      </Box>

      {/* Mensagem informativa sobre os dados - versão simples */}
      <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0' }}>
        <Typography 
          variant="caption" 
          color="text.secondary"
          sx={{ 
            display: 'block',
            textAlign: 'center',
            fontSize: '0.75rem',
            fontStyle: 'italic'
          }}
        >
          * Todas as informações exibidas nesta página são fornecidas diretamente pelas organizações cadastradas (ONGs). 
          A veracidade, atualização e precisão dos dados divulgados são de inteira responsabilidade das respectivas instituições.
        </Typography>
      </Box>

      <DonationModalComponent ngo={action.ong} />
      <VolunteerModalComponent ngo={action.ong} />
      
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={3000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
